import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useUser } from "../contexts/UserContext";
import { motion } from "framer-motion";
import Footer from '../components/Footer'
import { exportToWord } from "../utils/printUtils";
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from "../utils/printHelper";
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PrinterIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function LeavePermitForm() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const printRef = useRef();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [form, setForm] = useState({
    leave_type: "",
    start_date: "",
    end_date: "",
    leave_time: "",
    reason: "",
    substitute_name: "",
    notes: "",
    doctorNote: null,
    doctorNoteUrl: "",
  });

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");

  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage("");
      setToastType("");
    }, 3000);
  };

  useEffect(() => {
    if (userInfo?.id) {
      fetchApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from("leave_permit_forms")
        .select("*")
      
      // Admin bisa melihat semua, user biasa hanya milik sendiri
      if (userInfo.role !== 'admin') {
        query = query.eq("user_id", userInfo.id)
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch user info untuk admin
      let dataWithUsers = data;
      if (userInfo.role === 'admin' && data && data.length > 0) {
        const userIds = [...new Set(data.map(item => item.user_id))];
        const { data: usersData } = await supabase
          .from("users")
          .select("id, full_name, email")
          .in("id", userIds);
        
        dataWithUsers = data.map(item => ({
          ...item,
          user: usersData?.find(u => u.id === item.user_id) || null
        }));
      }

      setApplications(dataWithUsers || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
      showToast("Gagal memuat data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.leave_type || !form.start_date || !form.reason || !form.notes) {
      showToast("⚠️ Mohon lengkapi semua field yang wajib diisi", "error");
      return;
    }

    setIsLoading(true);

    try {
      // Calculate day names
      const startDate = new Date(form.start_date);
      const endDate = form.end_date ? new Date(form.end_date) : startDate;
      const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const days = [];
      const current = new Date(startDate);
      while (current <= endDate) {
        days.push(dayNames[current.getDay()]);
        current.setDate(current.getDate() + 1);
      }

      const payload = {
        user_id: userInfo.id,
        employee_name: userInfo.full_name || userInfo.email,
        division: userInfo.job || userInfo.role || "Staff",
        leave_type: form.leave_type,
        leave_day: days.join(', '),
        leave_date: form.start_date + (form.end_date ? ` - ${form.end_date}` : ''),
        leave_time: form.leave_time || "-",
        reason: form.reason,
        substitute_name: form.substitute_name || "-",
        notes: form.notes,
        status: userInfo.role === 'admin' ? 'approved' : 'pending',
        approved_by: userInfo.role === 'admin' ? userInfo.full_name : null,
        approved_at: userInfo.role === 'admin' ? new Date().toISOString() : null,
      };

      if (editingId) {
        const { error } = await supabase
          .from("leave_permit_forms")
          .update(payload)
          .eq("id", editingId);

        if (error) throw error;
        
        // Set success data untuk edit
        setSuccessData({
          isEdit: true,
          leaveType: getLeaveTypeLabel(form.leave_type),
          dates: form.start_date + (form.end_date ? ` - ${form.end_date}` : ''),
          reason: form.reason
        });
        setShowSuccessModal(true);
      } else {
        const { error } = await supabase
          .from("leave_permit_forms")
          .insert([payload]);

        if (error) throw error;
        
        // Set success data untuk modal
        setSuccessData({
          isEdit: false,
          isAutoApproved: userInfo.role === 'admin',
          leaveType: getLeaveTypeLabel(form.leave_type),
          dates: form.start_date + (form.end_date ? ` - ${form.end_date}` : ''),
          reason: form.reason
        });
        setShowSuccessModal(true);
      }

      fetchApplications();
      closeModal();
    } catch (error) {
      console.error("Error:", error);
      showToast(error.message || "Gagal menyimpan data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (application) => {
    // Parse dates from leave_date field
    const dates = application.leave_date.includes(' - ') 
      ? application.leave_date.split(' - ') 
      : [application.leave_date, ''];
    
    setForm({
      leave_type: application.leave_type,
      start_date: dates[0],
      end_date: dates[1],
      leave_time: application.leave_time === '-' ? '' : application.leave_time,
      reason: application.reason,
      substitute_name: application.substitute_name === '-' ? '' : application.substitute_name,
      notes: application.notes,
    });
    setEditingId(application.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus surat lamaran ini?")) return;

    try {
      const { error } = await supabase
        .from("leave_permit_forms")
        .delete()
        .eq("id", id);

      if (error) throw error;

      showToast("✅ Permohonan izin berhasil dihapus");
      fetchApplications();
    } catch (error) {
      console.error("Error:", error);
      showToast("Gagal menghapus data", "error");
    }
  };

  const handleApprove = async (id) => {
    try {
      const { error } = await supabase
        .from('leave_permit_forms')
        .update({ 
          status: 'approved',
          approved_at: new Date().toISOString(),
          approved_by: userInfo?.full_name
        })
        .eq('id', id);

      if (error) throw error;
      
      showToast("✅ Permohonan disetujui", "success");
      fetchApplications();
    } catch (error) {
      console.error("Error approving:", error);
      showToast("Gagal menyetujui permohonan", "error");
    }
  };

  const handleReject = async (id) => {
    setRejectingId(id);
    setShowRejectModal(true);
  };

  const submitRejection = async () => {
    if (!rejectionNote.trim()) {
      showToast("Mohon masukkan alasan penolakan", "error");
      return;
    }

    try {
      const { error } = await supabase
        .from('leave_permit_forms')
        .update({ 
          status: 'rejected',
          rejection_note: rejectionNote,
          rejected_by: userInfo.full_name,
          rejected_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', rejectingId);

      if (error) throw error;
      
      setShowRejectModal(false);
      setRejectionNote("");
      setRejectingId(null);
      showToast("❌ Permohonan ditolak", "success");
      fetchApplications();
    } catch (error) {
      console.error("Error rejecting:", error);
      showToast("Gagal menolak permohonan", "error");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({
      leave_type: "",
      start_date: "",
      end_date: "",
      leave_time: "",
      reason: "",
      substitute_name: "",
      notes: "",
    });
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      'early_return': 'Pulang Cepat',
      'late_arrival': 'Datang Terlambat',
      'cuti': 'Cuti',
      'other': 'Lainnya'
    };
    return labels[type] || type;
  };

  const handlePreview = (app) => {
    setPreviewData(app);
    setShowPreview(true);
  };

  const handlePrint = () => {
    // Deteksi mobile dan gunakan native print
    if (isMobileDevice()) {
      // Tampilkan hint untuk mobile user
      showToast('💡 Tip: Untuk hasil terbaik di mobile, gunakan Export ke Word', 'info');
      
      // Delay sedikit agar toast muncul
      setTimeout(() => {
        handleMobileFriendlyPrint({
          documentTitle: `Form_Izin_${previewData?.employee_name || 'Karyawan'}`,
          onAfterPrint: () => {
            showToast('Dokumen berhasil dicetak');
          }
        });
      }, 1000);
    } else {
      // Desktop: gunakan react-to-print
      handleDesktopPrint();
    }
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Form_Izin_${previewData?.employee_name || 'Karyawan'}`,
    onAfterPrint: () => {
      showToast('Dokumen berhasil dicetak');
    }
  });

  const handleExportWord = () => {
    exportToWord('printable-permit-form', `Form_Izin_${previewData?.employee_name || 'Karyawan'}_${new Date().getTime()}`);
    showToast('Dokumen berhasil diexport ke Word');
  };

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #printable-permit-form,
          #printable-permit-form * {
            visibility: visible !important;
          }
          #printable-permit-form {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>
      <div className="min-h-screen bg-white relative overflow-hidden no-print flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 relative">
                  <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
                  <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
                  <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                  </div>
                </div>
              </div>
              <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading leave permit forms...</p>
            </div>
          </div>
        ) : (
          <>
        {/* Soft Futuristic Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 via-blue-100/30 to-pink-100/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-blue-100/30 via-cyan-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-50/20 via-blue-50/20 to-pink-50/20 rounded-full blur-3xl animate-float-slow"></div>
          
          {/* Grid Pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.02) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(99, 102, 241, 0.02) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
        <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
              <div className="relative backdrop-blur-xl bg-gradient-to-r from-blue-600/90 via-blue-600/90 to-blue-600/90 p-6 sm:p-8 text-white rounded-t-3xl shadow-xl border border-white/20 overflow-hidden">
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer pointer-events-none"></div>
                
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 animate-pulse"></div>

                <div className="relative z-10">
                  <div className="flex justify-between items-center flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 sm:gap-4">
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-2.5 rounded-lg sm:rounded-xl shadow-sm transition-all duration-200 group "
                        title="Back to Dashboard"
                      >
                        <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                      </button>
                      <div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
                          Form Permohonan Izin
                        </h1>
                        <p className="text-blue-100 text-xs sm:text-sm mt-1 flex items-center gap-2">
                          <DocumentTextIcon className="w-4 h-4" />
                          <span>Kelola permohonan izin karyawan</span>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(true)}
                      className="flex items-center gap-2 bg-white hover:bg-blue-50 text-blue-700 px-4 py-2.5 rounded-xl shadow-sm font-medium transition-all duration-200 "
                    >
                      <PlusIcon className="w-5 h-5" />
                      <span>Buat Baru</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-6">
                {/* Total Permohonan Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-sm hover:shadow-md transition-all "
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium mb-1">Total Permohonan</p>
                      <p className="text-3xl font-bold text-white">{applications.length}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                      <DocumentTextIcon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-blue-400/30">
                    <p className="text-blue-100 text-xs">Semua permohonan izin</p>
                  </div>
                </motion.div>

                {/* Pending Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-amber-500 to-orange-600 p-6 rounded-xl shadow-sm hover:shadow-md transition-all "
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm font-medium mb-1">Menunggu</p>
                      <p className="text-3xl font-bold text-white">
                        {applications.filter(app => app.status === 'pending').length}
                      </p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                      <ClockIcon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-amber-400/30">
                    <p className="text-amber-100 text-xs">Menunggu persetujuan</p>
                  </div>
                </motion.div>

                {/* Approved Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 rounded-xl shadow-sm hover:shadow-md transition-all "
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm font-medium mb-1">Disetujui</p>
                      <p className="text-3xl font-bold text-white">
                        {applications.filter(app => app.status === 'approved').length}
                      </p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-emerald-400/30">
                    <p className="text-emerald-100 text-xs">Permohonan disetujui</p>
                  </div>
                </motion.div>

                {/* Rejected Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-br from-rose-500 to-red-600 p-6 rounded-xl shadow-sm hover:shadow-md transition-all "
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-rose-100 text-sm font-medium mb-1">Ditolak</p>
                      <p className="text-3xl font-bold text-white">
                        {applications.filter(app => app.status === 'rejected').length}
                      </p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-rose-400/30">
                    <p className="text-rose-100 text-xs">Permohonan ditolak</p>
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-b-2xl shadow-xl p-6 sm:p-8">
                {/* Table - Desktop */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-50 to-blue-50">
                      <tr>
                        {userInfo?.role === 'admin' && (
                          <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Dibuat Oleh</th>
                        )}
                        <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Tgl. Pengajuan</th>
                        <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Jenis Izin</th>
                        <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Periode Izin</th>
                        <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Keperluan</th>
                        <th className="px-4 py-3.5 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3.5 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {applications.length === 0 ? (
                        <tr>
                          <td colSpan={userInfo?.role === 'admin' ? "7" : "6"} className="px-4 py-12 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <div className="p-4 bg-slate-50 rounded-full">
                                <DocumentTextIcon className="w-12 h-12 text-slate-400" />
                              </div>
                              <p className="text-slate-500 font-medium">Belum ada permohonan izin</p>
                              <p className="text-sm text-slate-400">Klik tombol "Buat Baru" untuk mengajukan permohonan</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        applications.map((app) => {
                          const leaveTypeLabels = {
                            'early_return': 'Pulang Cepat',
                            'late_arrival': 'Datang Terlambat',
                            'cuti': 'Cuti',
                            'other': 'Lainnya'
                          };
                          const statusColors = {
                            'pending': 'bg-amber-100 text-amber-800 border border-amber-200',
                            'approved': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                            'rejected': 'bg-rose-100 text-rose-800 border border-rose-200'
                          };
                          return (
                            <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                              {userInfo?.role === 'admin' && (
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="font-semibold text-sm text-slate-800">{app.user?.full_name || 'N/A'}</p>
                                    <p className="text-xs text-slate-500">{app.user?.email || ''}</p>
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-3 text-sm text-slate-700">
                                {new Date(app.createdAt).toLocaleDateString("id-ID")}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {leaveTypeLabels[app.leave_type] || app.leave_type}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700">{app.leave_date}</td>
                              <td className="px-4 py-3 text-sm text-slate-700">{app.reason}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col gap-1">
                                  <span className={`px-3 py-1.5 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 ${statusColors[app.status] || 'bg-slate-50 text-slate-800 border border-gray-200'}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                      app.status === 'pending' ? 'bg-amber-500' : 
                                      app.status === 'approved' ? 'bg-emerald-500' : 
                                      'bg-rose-500'
                                    }`}></span>
                                    {app.status === 'pending' ? 'Menunggu Persetujuan' : app.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                                  </span>
                                  {app.status === 'rejected' && app.rejectionNote && (
                                    <span className="text-xs text-red-600 flex items-center gap-1">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                      </svg>
                                      Ada alasan
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handlePreview(app)}
                                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all hover:scale-110"
                                  title="Lihat & Print"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </button>
                                {app.status === 'pending' && userInfo?.role === 'admin' ? (
                                  <>
                                    <button
                                      onClick={() => handleApprove(app.id)}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all hover:scale-110"
                                      title="Setujui"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleReject(app.id)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all hover:scale-110"
                                      title="Tolak"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEdit(app)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all hover:scale-110"
                                      title="Edit"
                                    >
                                      <PencilIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(app.id)}
                                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-all hover:scale-110"
                                      title="Hapus"
                                    >
                                      <TrashIcon className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Cards - Mobile */}
                <div className="md:hidden space-y-4">
                  {applications.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-4 bg-slate-50 rounded-full">
                          <DocumentTextIcon className="w-12 h-12 text-slate-400" />
                        </div>
                        <p className="text-slate-500 font-medium">Belum ada permohonan izin</p>
                        <p className="text-sm text-slate-400">Klik tombol "Buat Baru" untuk mengajukan permohonan</p>
                      </div>
                    </div>
                  ) : (
                    applications.map((app) => {
                      const leaveTypeLabels = {
                        'early_return': 'Pulang Cepat',
                        'late_arrival': 'Datang Terlambat',
                        'cuti': 'Cuti',
                        'other': 'Lainnya'
                      };
                      const statusColors = {
                        'pending': 'bg-amber-100 text-amber-800 border border-amber-200',
                        'approved': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                        'rejected': 'bg-rose-100 text-rose-800 border border-rose-200'
                      };
                      return (
                        <div
                          key={app.id}
                          className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                        >
                          {userInfo?.role === 'admin' && app.user && (
                            <div className="pb-3 mb-3 border-b border-gray-200">
                              <p className="text-xs text-slate-500 mb-1">Dibuat Oleh:</p>
                              <p className="font-semibold text-sm text-slate-800">{app.user.full_name}</p>
                              <p className="text-xs text-slate-500">{app.user.email}</p>
                            </div>
                          )}
                          <div className="space-y-2.5 mb-4">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-bold text-gray-900 text-base">{leaveTypeLabels[app.leave_type] || app.leave_type}</h3>
                              <div className="flex flex-col items-end gap-1">
                                <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 whitespace-nowrap ${statusColors[app.status] || 'bg-slate-50 text-slate-800 border border-gray-200'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    app.status === 'pending' ? 'bg-amber-500' : 
                                    app.status === 'approved' ? 'bg-emerald-500' : 
                                    'bg-rose-500'
                                  }`}></span>
                                  {app.status === 'pending' ? 'Menunggu' : app.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                                </span>
                                {app.status === 'rejected' && app.rejectionNote && (
                                  <span className="text-xs text-red-600 flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    Ada alasan
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg space-y-1.5">
                              <p className="text-sm text-slate-700">
                                <span className="font-semibold text-gray-900">Tanggal:</span> {app.leave_date}
                              </p>
                              <p className="text-sm text-slate-700">
                                <span className="font-semibold text-gray-900">Hari:</span> {app.leave_day}
                              </p>
                            </div>
                            <p className="text-sm text-slate-700">
                              <span className="font-semibold text-gray-900">Keperluan:</span> {app.reason}
                            </p>
                            <p className="text-xs text-slate-500 pt-1 border-t border-gray-100">
                              Diajukan: {new Date(app.createdAt).toLocaleDateString("id-ID", { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <div className="flex gap-1 sm:gap-1.5">
                          <button
                            onClick={() => handlePreview(app)}
                            className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-all shadow-sm hover:shadow"
                          >
                            <EyeIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="hidden sm:inline">Lihat</span>
                          </button>
                          {app.status === 'pending' && userInfo?.role === 'admin' ? (
                            <>
                              <button
                                onClick={() => handleApprove(app.id)}
                                className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-all shadow-sm hover:shadow"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="hidden sm:inline">Setujui</span>
                              </button>
                              <button
                                onClick={() => handleReject(app.id)}
                                className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-all shadow-sm hover:shadow"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span className="hidden sm:inline">Tolak</span>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(app)}
                                className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-all shadow-sm hover:shadow"
                              >
                                <PencilIcon className="w-4 h-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Edit</span>
                              </button>
                              <button
                                onClick={() => handleDelete(app.id)}
                                className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-2 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white rounded-lg text-xs min-h-[40px] font-medium transition-all shadow-sm hover:shadow"
                              >
                                <TrashIcon className="w-4 h-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Hapus</span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Modal */}
          {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[95vh] flex flex-col p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-2xl">
              <div className="flex items-center gap-3 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b-2 border-gray-100 flex-shrink-0">
                <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                  <DocumentTextIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  {editingId ? "Edit Permohonan Izin" : "Permohonan Izin Baru"}
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-5">
                <div>
                  <label className="text-sm font-semibold text-slate-800 mb-3 block">
                    Jenis Izin <span className="text-rose-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'early_return', label: 'Pulang Cepat', icon: '🏃' },
                      { value: 'late_arrival', label: 'Datang Terlambat', icon: '⏰' },
                      { value: 'cuti', label: 'Cuti', icon: '🏖️' },
                      { value: 'other', label: 'Lainnya', icon: '📋' }
                    ].map((type) => (
                      <label
                        key={type.value}
                        className={`flex items-center gap-2 p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                          form.leave_type === type.value
                            ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="leave_type"
                          value={type.value}
                          checked={form.leave_type === type.value}
                          onChange={handleChange}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-lg">{type.icon}</span>
                        <span className="text-sm font-semibold text-slate-800">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-800 mb-2 block">
                      Tanggal Mulai <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        name="start_date"
                        value={form.start_date}
                        onChange={handleChange}
                        className="w-full pl-3 sm:pl-10 pr-3 py-3 text-sm border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 min-h-[44px] bg-white transition-all"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-800 mb-2 block">
                      Tanggal Selesai <span className="text-slate-400 text-xs font-normal">(Opsional)</span>
                    </label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        name="end_date"
                        value={form.end_date}
                        onChange={handleChange}
                        min={form.start_date}
                        className="w-full pl-3 sm:pl-10 pr-3 py-3 text-sm border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 min-h-[44px] bg-white transition-all"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-800 mb-2 block">
                    Waktu Izin <span className="text-slate-400 text-xs font-normal">(Opsional)</span>
                  </label>
                  <input
                    type="text"
                    name="leave_time"
                    value={form.leave_time}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all"
                    placeholder="Contoh: 08:00 - 12:00 atau 14:00"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-800 mb-2 block">
                    Keperluan <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all"
                    placeholder="Contoh: Keperluan pribadi, urusan keluarga, dll"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-800 mb-2 block">
                    PIC Pengganti <span className="text-slate-400 text-xs font-normal">(Opsional)</span>
                  </label>
                  <input
                    type="text"
                    name="substitute_name"
                    value={form.substitute_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all"
                    placeholder="Nama rekan kerja yang menggantikan"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-800 mb-2 block">
                    Catatan & Keterangan <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all resize-none"
                    placeholder="Catatan tambahan atau keterangan lainnya..."
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                    <span>💡</span>
                    <span>Jelaskan keperluan izin Anda dengan detail</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-8 pt-6 border-t-2 border-gray-100">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-semibold text-slate-700 hover:bg-gray-50 transition-all hover:border-gray-300"
                >
                  Batal
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 text-white rounded-xl font-semibold transition-all shadow-sm hover:shadow-md disabled:cursor-not-allowed"
                >
                  {isLoading ? "Menyimpan..." : editingId ? "Perbarui Permohonan" : "Ajukan Permohonan"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview & Print Modal */}
        {showPreview && previewData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex justify-center items-center z-[10000] p-4 print:hidden">
            <div className="bg-white w-full max-w-2xl h-auto max-h-[75vh] rounded-xl sm:rounded-2xl shadow-2xl flex flex-col relative print:max-h-none print:max-w-none print:shadow-none">
              {/* Header - No Print */}
              <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200 print:hidden bg-gradient-to-r from-blue-50 to-blue-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                      <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <h4 className="text-base sm:text-lg font-bold text-gray-900">Preview Form Izin</h4>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportWord}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                      title="Export ke Word"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8V7a5 5 0 0110 0v1m-7 4h4m-2-2v8m-4 0h8" />
                      </svg>
                      <span className="hidden sm:inline">Word</span>
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <PrinterIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Print</span>
                    </button>
                    <button
                      onClick={() => setShowPreview(false)}
                      className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                    >
                      Tutup
                    </button>
                  </div>
                </div>
              </div>

              {/* Form Content - Printable */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 print:p-8 print:overflow-visible print:h-auto">
                <div ref={printRef} className="printable-document document-container" id="printable-permit-form">
                  {/* A4 Paper Style with ADIDATA Format */}
                  <div className="document-page">
                    {/* Header - Company Logo and Date */}
                    <div className="document-header">
                      <div className="flex justify-between items-start mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <img 
                            src="/assets/logos/adidata.png" 
                            alt="PT. Adi Data Informatika" 
                            className="company-logo h-10 sm:h-12"
                          />
                        </div>
                        <div className="document-date text-xs sm:text-sm">
                          <p>Jakarta, {new Date(previewData.createdAt || Date.now()).toLocaleDateString("id-ID", { 
                            day: 'numeric',
                            month: 'long', 
                            year: 'numeric' 
                          })}</p>
                        </div>
                      </div>
                    </div>

                    {/* Title */}
                    <h1 className="document-title text-base sm:text-lg font-bold text-center mb-3 sm:mb-4">PERMOHONAN IZIN</h1>

                    {/* Employee Info Grid */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-3 sm:mb-6 avoid-break">
                      <div className="info-box border border-gray-300 p-2 sm:p-3 rounded">
                        <div className="field-label text-xs font-semibold text-gray-600">NAMA:</div>
                        <div className="field-value font-semibold text-xs sm:text-sm">{previewData.employee_name || userInfo.full_name || '-'}</div>
                      </div>
                      <div className="info-box border border-gray-300 p-2 sm:p-3 rounded">
                        <div className="field-label text-xs font-semibold text-gray-600">BAGIAN:</div>
                        <div className="field-value font-semibold text-xs sm:text-sm">{previewData.division || userInfo.job || userInfo.role || 'Staff'}</div>
                      </div>
                    </div>
                    
                    <h2 className="text-center font-bold mb-3 text-xs sm:text-sm uppercase">JENIS PERMOHONAN IZIN</h2>

                    {/* Leave Type Checkboxes */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 sm:mb-6">
                      {[
                        { value: 'early_return', label: 'PULANG CEPAT' },
                        { value: 'late_arrival', label: 'DATANG TERLAMBAT' },
                        { value: 'cuti', label: 'CUTI' },
                        { value: 'other', label: 'LAINNYA' }
                      ].map((type) => (
                        <div key={type.value} className="border-2 border-black p-1.5 sm:p-2 flex items-center justify-center gap-1 sm:gap-2">
                          <div className={`w-3 h-3 sm:w-4 sm:h-4 border-2 border-black flex items-center justify-center ${
                            previewData.leave_type === type.value ? 'bg-black' : 'bg-white'
                          }`}>
                            {previewData.leave_type === type.value && (
                              <span className="text-white text-[8px] sm:text-xs font-bold">✓</span>
                            )}
                          </div>
                          <span className="text-[9px] sm:text-xs font-semibold leading-tight">{type.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Leave Details */}
                    <div className="space-y-1.5 sm:space-y-3 mb-3 sm:mb-6">
                      <div className="flex text-xs sm:text-sm">
                        <div className="w-32 sm:w-40 font-semibold">Hari</div>
                        <div className="flex-1">: {previewData.leave_day || '-'}</div>
                      </div>
                      <div className="flex text-xs sm:text-sm">
                        <div className="w-32 sm:w-40 font-semibold">Tanggal</div>
                        <div className="flex-1">: {previewData.leave_date || '-'}</div>
                      </div>
                      <div className="flex text-xs sm:text-sm">
                        <div className="w-32 sm:w-40 font-semibold">Jam</div>
                        <div className="flex-1">: {previewData.leave_time || '-'}</div>
                      </div>
                      <div className="flex text-xs sm:text-sm">
                        <div className="w-32 sm:w-40 font-semibold">Untuk Keperluan</div>
                        <div className="flex-1">: {previewData.reason || '-'}</div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="mb-3 sm:mb-6">
                      <div className="flex text-xs sm:text-sm">
                        <div className="w-32 sm:w-40 font-semibold">Catatan & Keterangan</div>
                        <div className="flex-1">
                          <span>: </span>
                          <span className="leading-relaxed whitespace-pre-line inline-block align-top">
                            {previewData.notes || '-'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Rejection Note - Only show in preview if rejected */}
                    {previewData.status === 'rejected' && previewData.rejectionNote && (
                      <div className="mb-3 sm:mb-4 bg-red-50 border border-red-300 p-2 sm:p-3 rounded print:hidden">
                        <div className="flex items-start gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="font-bold text-red-800 text-xs sm:text-sm mb-1">❌ Alasan Penolakan:</p>
                            <p className="text-xs sm:text-sm text-slate-800 whitespace-pre-wrap">{previewData.rejectionNote}</p>
                            <div className="flex items-center gap-2 text-[10px] sm:text-xs text-slate-600 mt-1.5 pt-1.5 border-t border-red-200">
                              <span>Ditolak oleh: <strong>{previewData.rejectedBy}</strong></span>
                              {previewData.rejectedAt && (
                                <>
                                  <span>•</span>
                                  <span>{new Date(previewData.rejectedAt).toLocaleDateString("id-ID", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Signature Table */}
                    <div className="signature-section">
                      <table className="w-full text-xs sm:text-sm">
                        <thead>
                          <tr>
                            <th className="border border-dark p-1.5 sm:p-2 text-center text-[10px] sm:text-xs font-bold">PEMOHON</th>
                            <th className="border border-dark p-1.5 sm:p-2 text-center text-[10px] sm:text-xs font-bold">PIC PENGGANTI</th>
                            <th className="border border-dark p-1.5 sm:p-2 text-center text-[10px] sm:text-xs font-bold">DISETUJUI</th>
                            <th className="border border-dark p-1.5 sm:p-2 text-center text-[10px] sm:text-xs font-bold">MENYETUJUI</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-dark p-1.5 sm:p-2 h-16 sm:h-24 align-bottom text-center">
                              <div className="signature-name text-[10px] sm:text-sm">{previewData.employee_name || userInfo.full_name || '-'}</div>
                            </td>
                            <td className="border border-dark p-1.5 sm:p-2 h-16 sm:h-24 align-bottom text-center">
                              <div className="text-[10px] sm:text-sm">{previewData.substitute_name || '-'}</div>
                            </td>
                            <td className="border border-dark p-1.5 sm:p-2 h-16 sm:h-24 align-bottom text-center">
                              <div className="text-[10px] sm:text-sm">_______________</div>
                            </td>
                            <td className="border border-dark p-1.5 sm:p-2 h-16 sm:h-24 align-bottom text-center">
                              <div className="text-[10px] sm:text-sm">_______________</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Footer */}
                    <div className="document-footer mt-3 sm:mt-6 text-[9px] sm:text-xs text-gray-600 text-center">
                      <div>No. Formulir: FRM-HRD-002 | Tanggal: {new Date(previewData.createdAt || Date.now()).toLocaleDateString("id-ID")}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal Card */}
        {showSuccessModal && successData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, type: "spring" }}
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Success Icon Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center shadow-sm"
                >
                  <svg className="w-12 h-12 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <motion.path
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>
                <h2 className="text-2xl font-bold text-white mt-4">
                  {successData.isEdit ? "Berhasil Diperbarui!" : "Berhasil Diajukan!"}
                </h2>
                <p className="text-emerald-50 mt-2">
                  {successData.isEdit 
                    ? "Permohonan izin Anda telah berhasil diperbarui"
                    : successData.isAutoApproved 
                      ? "Permohonan izin Anda telah disetujui secara otomatis"
                      : "Permohonan izin Anda telah berhasil diajukan"
                  }
                </p>
              </div>

              {/* Detail Information */}
              <div className="p-6 space-y-4">
                {/* Status Badge */}
                {!successData.isEdit && (
                  <div className="flex justify-center">
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      successData.isAutoApproved 
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                        : 'bg-amber-100 text-amber-800 border border-amber-200'
                    }`}>
                      {successData.isAutoApproved ? '✓ Disetujui Otomatis' : '⏳ Menunggu Persetujuan'}
                    </span>
                  </div>
                )}

                {/* Information Cards */}
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-50 p-4 rounded-xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Jenis Izin</p>
                        <p className="text-sm font-bold text-blue-900 mt-1">{successData.leaveType}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-pink-50 p-4 rounded-xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 012 2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Periode</p>
                        <p className="text-sm font-bold text-blue-900 mt-1">{successData.dates}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-xl border border-orange-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Keperluan</p>
                        <p className="text-sm font-medium text-orange-900 mt-1">{successData.reason}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                {!successData.isEdit && !successData.isAutoApproved && (
                  <div className="bg-gradient-to-r from-slate-50 to-slate-50 p-4 rounded-xl border border-gray-200">
                    <p className="text-xs text-slate-600 font-semibold uppercase tracking-wide mb-2">Langkah Selanjutnya</p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span>Permohonan Anda akan direview oleh atasan</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span>Anda akan menerima notifikasi setelah disetujui</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span>Cek status di dashboard secara berkala</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="p-6 pt-0 space-y-3">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold transition-all shadow-sm hover:shadow-md"
                >
                  Kembali ke Dashboard
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    if (applications.length > 0) {
                      handlePreview(applications[0]);
                    }
                  }}
                  className="w-full px-6 py-3 border border-slate-200 hover:border-gray-300 text-slate-700 rounded-xl font-semibold transition-all hover:bg-gray-50"
                >
                  Lihat Detail Permohonan
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Rejection Note Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-800">Alasan Penolakan</h2>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionNote("");
                    setRejectingId(null);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Mohon berikan alasan penolakan <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-400"
                  placeholder="Contoh: Jumlah cuti tahun ini sudah habis, atau tidak ada pengganti yang tersedia..."
                ></textarea>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionNote("");
                    setRejectingId(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={submitRejection}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Tolak Permohonan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Note Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-slate-800">Alasan Penolakan</h2>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionNote("");
                    setRejectingId(null);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Mohon berikan alasan penolakan <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionNote}
                  onChange={(e) => setRejectionNote(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-400"
                  placeholder="Contoh: Tidak ada pengganti yang tersedia, atau dokumen pendukung tidak lengkap..."
                ></textarea>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionNote("");
                    setRejectingId(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={submitRejection}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Tolak Permohonan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toastMessage && (
          <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
            <div
              className={`px-6 py-3 rounded-lg shadow-sm ${
                toastType === "error"
                  ? "bg-red-500 text-white"
                  : "bg-green-500 text-white"
              }`}
            >
              {toastMessage}
            </div>
          </div>
        )}
        </main>
        <Footer />
        </>
        )}
      </div>
    </>
  );
}